import api from "../../../services/api.service"

/**
 * The backend answers errors as {status:false, message:"..."} with HTTP 400,
 * so the useful text is response.data.message — error.message is only axios'
 * generic "Request failed with status code 400".
 */
export function errorMessage(error) {
    if (error == null) return 'Unknown error';
    if (typeof error === 'string') return error;
    const data = error.response && error.response.data;
    if (data && typeof data.message === 'string' && data.message.trim() !== '') return data.message;
    if (typeof error.message === 'string') return error.message;
    return String(error);
}

/** one place for the hub calls: POST, unwrap objects, reject with the readable reason */
function hub(url, params, body) {
    return new Promise((resolve, reject) => {
        api.sendRequest('whatsapp/hub/' + url, params || null, body || null)
            .then(resolve, error => reject(errorMessage(error)));
    });
}

export const whatsappModule = {
    namespaced: true,
    state: {
        /** templates cache per branch — the composer and the gallery share it */
        templatesByBranch: {},
        tags: [],
        /** chats with an unopened guest message across the selected branches — the menu badge */
        unread: 0,
    },
    getters: {
        totalUnread(state) {
            return state.unread > 0 ? state.unread : '';
        },
        canReadWhatsapp(state, getters, rootState, rootGetters) {
            return rootGetters['user/canRead']('whatsapp');
        },
        canEditWhatsapp(state, getters, rootState, rootGetters) {
            return rootGetters['user/canEdit']('whatsapp');
        },
        hubTemplates: (state) => (buid) => state.templatesByBranch[buid] || null,
        hubTags: (state) => state.tags,
    },
    mutations: {
        setTemplates(state, {buid, templates}) {
            state.templatesByBranch = {...state.templatesByBranch, [buid]: templates};
        },
        setTags(state, tags) {
            state.tags = tags || [];
        },
        setUnread(state, total) {
            state.unread = total || 0;
        },
    },
    actions: {
        getData() {
            return new Promise(function (resolve) {
                resolve();
            })
        },

        /* ── WhatsApp hub ────────────────────────────────────────────── */

        /** menu badge; silent — a failed poll must never toast */
        refreshUnread({commit, rootGetters}) {
            const buids = rootGetters['data/getSelectedBranchesIds'] || [];
            return hub('unread', {buids: buids.join(',')}).then(ans => {
                commit('setUnread', ans['unread'] || 0);
                return ans['unread'] || 0;
            }).catch(() => null);
        },
        hubStatus(actions, {buid}) {
            return hub('status', {buid}).then(ans => ans['status']);
        },
        hubSearchClients(actions, {buid, q}) {
            return hub('clients/search', {buid, q}).then(ans => ans['clients'] || []);
        },
        hubSettings(actions, {buid}) {
            return hub('settings', {buid}).then(ans => ans['settings']);
        },
        hubSaveSettings({dispatch}, {buid, autoReply, autoReplyText}) {
            return hub('settings/save', {buid, autoReply: !!autoReply, autoReplyText: autoReplyText || ''}).then(ans => {
                dispatch('notifications/success', {
                    title: {text: 'WhatsApp'},
                    message: {text: '%s saved successfully', arguments: ['Settings']}
                }, {root: true});
                return ans['settings'];
            }, error => {
                dispatch('notifications/error', {title: {text: 'WhatsApp'}, message: error}, {root: true});
                return Promise.reject(error);
            });
        },
        hubRegisterWebhooks({dispatch}, {buid}) {
            return hub('webhooks/register', {buid}).then(ans => {
                dispatch('notifications/success', {
                    title: {text: 'WhatsApp'},
                    message: {text: '%s registered successfully', arguments: ['Webhooks']}
                }, {root: true});
                return ans['status'];
            }, error => {
                dispatch('notifications/error', {title: {text: 'WhatsApp'}, message: error}, {root: true});
                return Promise.reject(error);
            });
        },
        hubTags({commit, state}) {
            if (state.tags.length > 0) return Promise.resolve(state.tags);
            return hub('tags').then(ans => {
                commit('setTags', ans['tags']);
                return ans['tags'];
            });
        },
        hubAudience(actions, request) {
            return hub('audience', null, request);
        },
        hubPreview(actions, {clientId, contractId}) {
            return hub('preview', {clientId, contractId: contractId || undefined}).then(ans => ans['tags'] || {});
        },
        hubSend({dispatch}, request) {
            return hub('send', null, request).then(ans => {
                const sent = ans['sent'] || 0, failed = ans['failed'] || 0;
                const scheduled = (ans['results'] || []).some(r => r.scheduled);
                if (failed === 0) {
                    dispatch('notifications/success', {
                        title: {text: 'WhatsApp'},
                        message: {text: scheduled ? '%s message(s) scheduled' : '%s message(s) sent', arguments: [sent]}
                    }, {root: true});
                } else {
                    dispatch('notifications/error', {
                        title: {text: 'WhatsApp'},
                        message: {text: '%s sent, %s failed', arguments: [sent, failed]}
                    }, {root: true});
                }
                return ans;
            }, error => {
                dispatch('notifications/error', {title: {text: 'WhatsApp'}, message: error}, {root: true});
                return Promise.reject(error);
            });
        },
        hubTemplates({commit, state}, {buid, force}) {
            if (!force && state.templatesByBranch[buid] != null) return Promise.resolve(state.templatesByBranch[buid]);
            return hub('templates', {buid}).then(ans => {
                const templates = ans['templates'] || [];
                commit('setTemplates', {buid, templates});
                return templates;
            });
        },
        hubTemplate({commit, state}, {buid, sid}) {
            return hub('templates/get', {buid, sid}).then(ans => {
                const template = ans['template'];
                const cached = state.templatesByBranch[buid];
                if (template != null && cached != null) {
                    commit('setTemplates', {buid, templates: cached.map(t => t.sid === template.sid ? template : t)});
                }
                return template;
            });
        },
        hubCreateTemplate({commit, state, dispatch}, request) {
            return hub('templates/create', null, request).then(ans => {
                const template = ans['template'];
                const cached = state.templatesByBranch[request.buid];
                if (template != null && cached != null) {
                    commit('setTemplates', {buid: request.buid, templates: [template, ...cached]});
                }
                dispatch('notifications/success', {
                    title: {text: 'WhatsApp'},
                    message: {text: '%s created successfully', arguments: ['Template']}
                }, {root: true});
                return template;
            }, error => {
                dispatch('notifications/error', {title: {text: 'WhatsApp'}, message: error}, {root: true});
                return Promise.reject(error);
            });
        },
        hubSubmitTemplate({dispatch}, {buid, sid, name, category}) {
            return hub('templates/submit', {buid, sid, name, category}).then(ans => {
                dispatch('notifications/success', {
                    title: {text: 'WhatsApp'},
                    message: {text: '%s submitted for approval', arguments: ['Template']}
                }, {root: true});
                return ans['approval'];
            }, error => {
                dispatch('notifications/error', {title: {text: 'WhatsApp'}, message: error}, {root: true});
                return Promise.reject(error);
            });
        },
        hubDeleteTemplate({commit, state, dispatch}, {buid, sid}) {
            return hub('templates/delete', {buid, sid}).then(() => {
                const cached = state.templatesByBranch[buid];
                if (cached != null) commit('setTemplates', {buid, templates: cached.filter(t => t.sid !== sid)});
                dispatch('notifications/success', {
                    title: {text: 'WhatsApp'},
                    message: {text: '%s deleted successfully', arguments: ['Template']}
                }, {root: true});
            }, error => {
                dispatch('notifications/error', {title: {text: 'WhatsApp'}, message: error}, {root: true});
                return Promise.reject(error);
            });
        },
        hubConversations(actions, {buids}) {
            return hub('conversations', {buids: (buids || []).join(',')});
        },
        hubConversation(actions, {clientId, seen, sync}) {
            return hub('conversation/' + clientId, {seen: !!seen, sync: !!sync});
        },
        hubScheduled(actions, {buids, pendingOnly}) {
            return hub('scheduled', {buids: (buids || []).join(','), pendingOnly: !!pendingOnly}).then(ans => ans['scheduled'] || []);
        },
        hubCancelScheduled({dispatch}, {batchId}) {
            return hub('scheduled/cancel', {batchId}).then(ans => {
                dispatch('notifications/success', {
                    title: {text: 'WhatsApp'},
                    message: {text: '%s message(s) cancelled', arguments: [ans['cancelled'] || 0]}
                }, {root: true});
                return ans['cancelled'];
            });
        },

        /* ── legacy (client modal chat, automated templates) ─────────── */

        getAllMessages(actions) {
            return new Promise(function (resolve, reject) {
                api.sendRequest('whatsapp/unseen', {
                    buids: actions.rootGetters['data/getSelectedBranchesIds'].join(","),
                }).then(
                    (ans) => {
                        if (ans["messages"] == null)
                            return reject('Could not get Messages ');
                        resolve(ans);
                    }, (error) => {
                        reject(error.message)
                    })
            })
        },
        getMessagesByClientId(actions, clientId) {
            return new Promise(function (resolve, reject) {
                api.sendRequest('whatsapp/id/' + clientId).then(
                    (ans) => {
                        resolve(ans);
                    }, (error) => {
                        reject(error.message)
                    })
            })
        },
        sendWhatsappMessage(actions, {newMessage, template}) {
            // Build WAMessageRequest payload
            const payload = {
                body: newMessage.body || '',
                clientId: newMessage.clientId,
                isTemplate: !!template,
                ...(template ? {
                    templateName: newMessage.templateName,
                    templateLanguage: newMessage.templateLanguage || 'en',
                    contentSid: newMessage.contentSid,
                } : {})
            };

            return new Promise(function (resolve, reject) {
                api.sendRequest('whatsapp/send/' + (!!template), null, payload).then(
                    (ans) => {
                        if (ans["messages"] == null)
                            return reject('Could not send message ');
                        actions.dispatch('notifications/success',
                            {
                                title: {text: 'Whatsapp'},
                                message: {text: '%s sent successfully', arguments: ['Message']}
                            },
                            {root: true});
                        resolve(ans);
                    },
                    (error) => {
                        actions.dispatch('notifications/error',
                            {
                                title: {text: 'Whatsapp '},
                                message: errorMessage(error)
                            },
                            {root: true});
                        reject(errorMessage(error));
                    }
                )
            })
        },

        getWhatsappTemplates(actions, {clientId}) {
            return new Promise(function (resolve, reject) {
                api.sendRequest('whatsapp/templates/' + clientId, null, null).then(
                    (ans) => {
                        if (ans["response"] == null)
                            return reject("No WhatsApp templates were returned for this branch");
                        resolve(ans);
                    }, (error) => {
                        actions.dispatch('notifications/error',
                            {
                                title: {text: 'Whatsapp Templates'},
                                message: errorMessage(error)
                            },
                            {root: true});
                        reject(errorMessage(error));
                    });
            });
        },
        getWhatsappTemplatesByBranch(actions, {branchId}) {
            return new Promise(function (resolve, reject) {
                api.sendRequest('whatsapp/templatesByBranch/' + branchId, null, null).then(
                    (ans) => {
                        if (ans["response"] == null)
                            return reject("No WhatsApp templates were returned for this branch");
                        resolve(ans);
                    }, (error) => {
                        actions.dispatch('notifications/error',
                            {
                                title: {text: 'Whatsapp Templates'},
                                message: errorMessage(error)
                            },
                            {root: true});
                        reject(errorMessage(error));
                    });
            });
        },
        getWhatsappList() {
            return new Promise(function (resolve, reject) {
                api.sendRequest('whatsapp/getWhatsappList', null, null).then(
                    (ans) => {
                        if (ans["Whatsapps"] == null)
                            return reject('Could not get whatsapps ');
                        resolve(ans["Whatsapps"]);
                    }, (error) => {
                        reject(error.message)
                    })
            })
        },
        getWhatsappById(actions, id) {
            return new Promise((resolve, reject) => {
                api.sendRequest('whatsapp/id/' + id, {}).then(
                    (ans) => {
                        if (ans["whatsapp"] == null)
                            return reject('Could not get whatsapp ');
                        resolve(ans["whatsapp"]);
                    }, (error) => {
                        reject(error.message)
                    })
            })
        },
        updateWhatsapp(actions, whatsapp) {
            return new Promise((resolve, reject) => {
                api.sendRequest('whatsapp/update', null, whatsapp).then(
                    (ans) => {
                        actions.dispatch('notifications/success',
                            {title: 'Update whatsapp', message: 'Whatsapp updated successfully'},
                            {root: true});
                        if (ans["whatsappId"] == null)
                            return reject('Could not update whatsapp ');
                        resolve(ans["whatsappId"]);
                    }, (error) => {
                        reject(error.message)
                    })
            })
        },
        createWhatsapp(actions, whatsapp) {
            return new Promise((resolve, reject) => {
                api.sendRequest('whatsapp/add', null, whatsapp).then(
                    (ans) => {
                        actions.dispatch('notifications/success',
                            {title: 'Add whatsapp', message: 'Whatsapp added successfully'},
                            {root: true});
                        if (ans["whatsappId"] == null)
                            return reject('Could not create whatsapp ');
                        resolve(ans["whatsappId"]);
                    }, (error) => {
                        reject(error.message)
                    })
            })
        },
    },
};
